import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createGame } from '@/integrations/supabase/game/gameCreateService';
import { useAuth } from '@/context/AuthContext';

export const useLoneWolf = (homeCourseId: string | null) => {
  const { user } = useAuth();
  const lastCheckedCourseId = useRef<string | null>(null);
  const [loneWolfVersion, setLoneWolfVersion] = useState(0);

  useEffect(() => {
    if (!user || !homeCourseId || lastCheckedCourseId.current === homeCourseId) return;
    lastCheckedCourseId.current = homeCourseId;

    const ensureLoneWolf = async () => {
      const currentYear = new Date().getFullYear();

      // Check for Lone Wolf games on this specific course
      const { data: existingLoneWolves } = await supabase
        .from('games')
        .select('id, is_recurring, recurring_group_id, start_date, end_date')
        .eq('is_lone_wolf', true)
        .eq('user_id', user.id)
        .eq('course_id', homeCourseId)
        .limit(10);

      if (existingLoneWolves && existingLoneWolves.length > 0) {
        // If a recurring one already exists, we're done — useRecurringGames handles yearly instances
        const hasRecurring = existingLoneWolves.some(g => g.is_recurring);
        if (hasRecurring) return;

        // Non-recurring Lone Wolf exists — upgrade it to recurring
        const loneWolf = existingLoneWolves[0];
        const groupId = crypto.randomUUID();
        const lwYear = new Date(loneWolf.start_date).getUTCFullYear();

        const { error: upgradeError } = await supabase
          .from('games')
          .update({
            is_recurring: true,
            recurring_group_id: groupId,
            duration_type: 'yearly',
            start_date: `${lwYear}-01-01T00:00:00.000Z`,
            end_date: `${lwYear}-12-31T23:59:59.999Z`,
          })
          .eq('id', loneWolf.id);

        if (upgradeError) {
          console.error('useLoneWolf - Failed to upgrade Lone Wolf to recurring:', upgradeError);
          return;
        }

        // Signal that game was updated so consumers can refetch
        setLoneWolfVersion(v => v + 1);
        return;
      }

      // No Lone Wolf at all — create the first recurring instance.
      // useRecurringGames will auto-create subsequent years.
      const result = await createGame({
        name: `Lone Wolf ${currentYear}`,
        courseId: homeCourseId,
        players: [{
          id: user.id,
          name: user.user_metadata?.name || user.email?.split('@')[0] || 'Me',
          email: user.email,
        }],
        durationType: 'yearly',
        isLoneWolf: true,
        isRecurring: true,
        recurringGroupId: crypto.randomUUID(),
        startDate: `${currentYear}-01-01T00:00:00.000Z`,
        endDate: `${currentYear}-12-31T23:59:59.999Z`,
      });

      if (result.error) {
        console.error('useLoneWolf - Failed to create Lone Wolf game:', result.error);
        return;
      }

      // Signal that a new game was created so consumers can refetch
      setLoneWolfVersion(v => v + 1);
    };

    ensureLoneWolf();
  }, [user, homeCourseId]);

  return { loneWolfVersion };
};
